import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultFastTextField } from 'src/components/mui-overrides/Textfield';
import { LaunchpadCard } from 'src/components/ui/LaunchpadCard';
import { isSmallMobileDeviceQuery } from 'src/util/responsive';
import { Coordinations } from './Coordinates';
import { AssetTypeConditional } from 'src/components/user/AssetTypeConditional';
import { RealEstatesPropertyTypeSelector } from './RealEstatePropertyTypeSelector';
import { PowerplantPropertyTypeSelector } from './PowerplantPropertyTypeSelector';
import { CountryCodeSelector } from './CountryCodeSelector';
import { CityTextField } from '../atomic-components/CityTextField';
import { PostalCodeTextField } from '../atomic-components/PostalCodeTextField';
import { AddressTextField } from '../atomic-components/AddressTextField';
import { ConstructionStatusSelector } from '../atomic-components/ConstructionStatusSelector';
import { TitleTextField } from '../atomic-components/TitleTextfield';
import { PropertyQuoteTextField } from '../atomic-components/PropertyQuoteTextField';
import { OverviewTextField } from '../atomic-components/OverviewTextfield';
import { PowerPlantBusinessTypeSelector } from '../atomic-components/PowerPlantBusinessSelector';

interface Props {
  disabled: boolean;
}

// dashboard.assets.addedit.main.generalinfo.fields.assetname.label
export const AssetGeneralInfo: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation();
  const isSmallMobileDevice = useMediaQuery(isSmallMobileDeviceQuery);

  return (
    <LaunchpadCard title={t('dashboard.assets.addedit.main.generalinfo.title')}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isSmallMobileDevice ? '1fr' : '1fr 1fr',
          columnGap: isSmallMobileDevice ? 'unset' : '1.5rem',
        }}
      >
        <TitleTextField disabled={disabled} />

        <ConstructionStatusSelector disabled={disabled} />

        <PowerPlantBusinessTypeSelector disabled={disabled} />

        <AddressTextField disabled={disabled} />

        <PropertyQuoteTextField disabled={disabled} />

        <AssetTypeConditional supported={['realEstate', 'powerplant', 'powerplantPortfolio', 'citizenParticipationLoan']}>
          <DefaultFastTextField
            disabled={disabled}
            type="number"
            label={t('dashboard.assets.addedit.main.generalinfo.fields.floor.label')}
            fieldName="location.floor"
            placeholder={t('dashboard.assets.addedit.main.generalinfo.fields.floor.placeholder')}
          />
        </AssetTypeConditional>

        <AssetTypeConditional supported={['realEstate', 'powerplant', 'powerplantPortfolio', 'citizenParticipationLoan']}>
          <DefaultFastTextField
            disabled={disabled}
            type="text"
            label={t('dashboard.assets.addedit.main.generalinfo.fields.door.label')}
            fieldName="location.door"
            placeholder={t('dashboard.assets.addedit.main.generalinfo.fields.floor.placeholder')}
          />
        </AssetTypeConditional>

        <CityTextField disabled={disabled} />

        <PostalCodeTextField disabled={disabled} />

        <CountryCodeSelector />

        <Coordinations />

        <AssetTypeConditional supported={['powerplant', 'powerplantPortfolio', 'citizenParticipationLoan']}>
          <PowerplantPropertyTypeSelector disabled={disabled} />
        </AssetTypeConditional>

        <AssetTypeConditional supported={['realEstate', 'builderOwnerModel']}>
          <RealEstatesPropertyTypeSelector disabled={disabled} />
        </AssetTypeConditional>

        <AssetTypeConditional supported={['builderOwnerModel']}>
          <OverviewTextField />
        </AssetTypeConditional>
      </div>
    </LaunchpadCard>
  );
};
